import React, { useState, useRef, useEffect, useCallback } from 'react'; // eslint-disable-line
import general from '../../../../../../general';
import Dialog from '../../../../../../lib/Dialog';
import { useStateContext, ComplexAvatarContainer } from '@xkit-yx/im-kit-ui';
import './css/Search.css';

// 高亮消息内容中的关键词
function HighlightText({ text, keyword }) {
  if (!keyword || !text) return <span>{text}</span>;
  const parts = text.split(new RegExp(`(${keyword})`, 'gi'));
  return (
    <span>
      {parts.map((part, i) =>
        part.toLowerCase() === keyword.toLowerCase()
          ? <span key={i} className="sch-highlight">{part}</span>
          : part
      )}
    </span>
  );
}

export default function Search(props) {
  const { tid } = props.query;
  // sessionId 可从 URL 读取，也可从 tid 推导（TeamSetting 跳转时只传 tid）
  const sessionId = props.query.sessionId || `team-${tid}`;
  const { nim } = useStateContext();
  // input 改为非受控，通过 ref 读取值，避免受控组件在 setTitle 里值不更新的问题
  const [results, setResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [allLoaded, setAllLoaded] = useState(false);
  const [displayKeyword, setDisplayKeyword] = useState(''); // 仅用于高亮显示
  const lastMsgTimeRef  = useRef(null);  // 分页游标：上批最后一条的 time
  const lastMsgIdRef    = useRef(null);  // 分页游标：上批最后一条的 idServer
  const loadingRef      = useRef(false); // 与 loading state 同步，供 Observer 回调读取最新值
  const allLoadedRef    = useRef(false); // 与 allLoaded state 同步
  const hasSearchedRef  = useRef(false); // 与 hasSearched state 同步
  const inputRef = useRef(null);
  // 用 ref 保存最新的 handleSearch，供 setOperate 按钮调用（避免持有旧闭包）
  const handleSearchRef = useRef(null);

  // 群创建时间戳（用于日历页限制最早可选日期）
  const [createTime, setCreateTime] = useState(0);
  const createTimeFetchedRef = useRef(false);

  useEffect(() => {
    if (!props.active || !tid || createTimeFetchedRef.current) return;
    createTimeFetchedRef.current = true;
    general.axios.post('/base/yunxin/getClusterInfo', { tid }).then((res) => {
      const { status, data } = res.data;
      if (status.succeed) {
        // 字段名确认后替换；若接口还未返回该字段则保持初始测试值不变
        // create_time 为秒级时间戳，转为毫秒
        const ct = data.info.create_time;
        if (ct) setCreateTime(ct * 1000);
      }
    }).catch(() => {});
  }, [props.active, tid]); // eslint-disable-line

  useEffect(() => {
    if (props.active) {
      props.setTitle(
        <div className="sch-header">
          <div className="sch-bar">
            <img className="sch-bar-icon" src="/resources/user/ss.png" alt="" />
            {/* 非受控 input：不绑定 value，用户可自由输入 */}
            <input
              ref={inputRef}
              type="text"
              className="sch-input"
              placeholder="请输入关键字"
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.target.blur(); // 先收起键盘
                  setTimeout(() => handleSearchRef.current?.(), 300); // 键盘落下后再搜索
                }
              }}
            />
          </div>
        </div>
      );
      props.setOperate(
        <div className="sch-search-btn" onClick={() => handleSearchRef.current?.()}>搜索</div>
      );
      // 进入页面自动聚焦
      setTimeout(() => inputRef.current?.focus(), 150);
    }
  }, [props.active]); // eslint-disable-line

  // 每批加载 100 条文本消息，客户端过滤关键词，直到凑够 PAGE_SIZE 条匹配或无更多数据
  const PAGE_SIZE  = 20;
  // 每次 doSearch 最多拉取的批次数，避免连发大量请求触发云信频控
  const MAX_BATCHES = 5;
  // 批次间延迟（ms），进一步降低请求频率
  const BATCH_DELAY = 200;

  const doSearch = async (kw, loadMore = false) => {
    if (!kw || !kw.trim()) return;
    if (loadingRef.current) return; // 防止并发重复调用
    const keyword = kw.trim().toLowerCase();
    loadingRef.current = true;
    setLoading(true);
    try {
      const matched   = [];
      let endTime     = loadMore ? lastMsgTimeRef.current : undefined;
      let lastMsgId   = loadMore ? lastMsgIdRef.current   : undefined;
      let exhausted   = false;
      let batchCount  = 0;

      // 循环拉取：凑够 PAGE_SIZE 条匹配 / 历史消息拉完 / 达到本次最大批次数 则停止
      while (matched.length < PAGE_SIZE && !exhausted && batchCount < MAX_BATCHES) {
        if (batchCount > 0) {
          await new Promise(resolve => setTimeout(resolve, BATCH_DELAY));
        }

        const batch = await nim.getHistoryMsgs({
          scene:    'team',
          to:       tid,
          msgTypes: ['text'],
          limit:    100,
          asc:      false,
          endTime,
          lastMsgId,
        });
        batchCount++;

        if (!batch || batch.length === 0) { exhausted = true; break; }

        batch.forEach(msg => {
          if (msg.body && msg.body.toLowerCase().includes(keyword)) {
            matched.push(msg);
          }
        });

        if (batch.length < 100) {
          exhausted = true;
        } else {
          const last = batch[batch.length - 1];
          endTime   = last.time;
          lastMsgId = last.idServer;
        }
      }

      // 保存游标供下次加载更多（未耗尽时游标有效，用户可继续 loadMore）
      lastMsgTimeRef.current = endTime   ?? null;
      lastMsgIdRef.current   = lastMsgId ?? null;

      if (loadMore) {
        setResults(prev => [...prev, ...matched]);
      } else {
        setResults(matched);
      }
      hasSearchedRef.current = true;
      allLoadedRef.current   = exhausted;
      setHasSearched(true);
      setDisplayKeyword(kw.trim());
      setAllLoaded(exhausted);
    } catch (err) {
      Dialog.error(err.message || '搜索失败');
    } finally {
      loadingRef.current = false;
      setLoading(false);
      // setTimeout 0：等 React commit 完成后再关闭 loading 遮罩，
      // 避免遮罩关闭动画时 DOM 还未更新导致短暂白屏
      if (!loadMore) setTimeout(() => Dialog.close(), 0);
    }
  };

  const handleSearch = () => {
    const kw = inputRef.current?.value || '';
    lastMsgTimeRef.current = null;
    lastMsgIdRef.current   = null;
    Dialog.loading();
    doSearch(kw, false);
  };
  // 每次渲染都更新 ref，保证 setOperate 里始终调用最新版本
  handleSearchRef.current = handleSearch;

  const handleLoadMore = useCallback(() => {
    const kw = inputRef.current?.value || displayKeyword;
    doSearch(kw, true);
  // eslint-disable-next-line
  }, [displayKeyword]);

  // 底部哨兵：进入视口时自动加载更多
  // 依赖中不包含 loading —— 用 loadingRef 在回调内读取最新值，避免 loading 变化时重建
  // Observer 导致的无限循环（loading: false → 重建 → 哨兵可见 → 再触发 → loading: false → …）
  const sentinelRef = useRef(null);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !loadingRef.current && !allLoadedRef.current && hasSearchedRef.current) {
        handleLoadMore();
      }
    }, { threshold: 0.1 });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore]);

  const formatDate = (time) =>
    new Date(time).toLocaleDateString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    }).replace(/\//g, '-');

  return (
    <div className="sch-page">      

      {/* 初始状态：日期 / 图片·视频 入口 */}
      {!hasSearched && (
        <div className="sch-shortcuts">
          <div
            className="sch-shortcut-item"
            onClick={() => general.history.push(`/chatHistory/dateSearch?tid=${tid}&sessionId=${sessionId}&createTime=${createTime}`)}
          >
            日期
          </div>
          <div className="sch-shortcut-divider" />
          <div
            className="sch-shortcut-item"
            onClick={() => general.history.push(`/chatHistory/mediaSearch?tid=${tid}&sessionId=${sessionId}`)}
          >
            图片/视频
          </div>
        </div>
      )}

      {/* 搜索结果 */}
      {hasSearched && (
        <div className="sch-results">
          {results.length === 0 && !loading && (
            <div className="sch-empty">未找到相关内容</div>
          )}

          {results.map((msg, idx) => (
            <div
              key={msg.idClient || idx}
              className="sch-result-item"
              onClick={() => {
                general.history.push(
                  `/chatHistoryViewer?tid=${tid}&time=${msg.time}&idClient=${encodeURIComponent(msg.idClient)}`
                );
              }}
            >
              <div className="sch-result-avatar">
                <ComplexAvatarContainer account={msg.from} size={40} canClick={false} />
              </div>
              <div className="sch-result-main">
                <div className="sch-result-header">
                  <span className="sch-result-nick">{msg.fromNick || msg.from}</span>
                  <span className="sch-result-date">{formatDate(msg.time)}</span>
                </div>
                <div className="sch-result-content">
                  <HighlightText text={msg.text || ''} keyword={displayKeyword} />
                </div>
              </div>
            </div>
          ))}

          

          {/* 底部哨兵：进入视口自动触发加载更多 */}
          {hasSearched && (
            <div ref={sentinelRef} className="sch-sentinel">
              {allLoaded && results.length > 0 && (
                <span className="sch-all-loaded">全部加载完毕</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
